import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { auth } from "../supabase/client";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import AuthPageBottomAnimation from "../components/AuthPageBottomAnimation";
import { establishRecoverySession } from "../utils/auth/passwordReset";
import {
  authAlertErrorSx,
  authAlertSuccessSx,
  authAlertWarningSx,
  authCardShellSx,
  authFieldSx,
  authLogoSx,
  authPageOuterSx,
  authPageContainerSx,
} from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import { getMm } from "../theme/modernMaterialEnterprise";

const PasswordResetContent = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx, outlineButtonSx } = useSystemButtonStyles();
  const fieldSx = useMemo(() => authFieldSx(isDarkMode), [isDarkMode]);
  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);
  const logoSx = useMemo(() => authLogoSx(isDarkMode), [isDarkMode]);
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isValidatingLink, setIsValidatingLink] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsValidatingLink(true);
      const result = await establishRecoverySession(auth);

      if (cancelled) return;

      if (result.ok) {
        setIsValidSession(true);
        setMessage("");
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setMessage(
          t("password_reset_link_invalid") ||
            "Invalid or expired reset link. Please request a new password reset."
        );
      }
      setIsValidatingLink(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return t("password_reset_password_min_length") || "Password must be at least 6 characters long.";
    }
    if (password.length > 128) {
      return t("password_reset_password_max_length") || "Password must be less than 128 characters.";
    }
    return null;
  };

  const handleNewPassword = async () => {
    if (!isValidSession) {
      setMessage(
        t("password_reset_link_invalid") ||
          "Invalid or expired reset link. Please request a new password reset."
      );
      return;
    }

    if (!newPassword.trim()) {
      setMessage(t("password_reset_enter_new") || "Please enter a new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage(t("password_reset_passwords_mismatch") || "Passwords do not match.");
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setMessage(passwordError);
      return;
    }

    setIsLoading(true);
    setMessage("");
    setIsSuccess(false);

    try {
      const { error } = await auth.updateUser({
        password: newPassword.trim(),
      });

      if (error) {
        if (error.message?.includes("session_not_found")) {
          setMessage(t("password_reset_link_invalid"));
        } else if (error.message?.includes("weak_password")) {
          setMessage(t("password_reset_weak_password") || "Password is too weak. Please choose a stronger password.");
        } else {
          setMessage(error.message || t("password_reset_update_failed") || "Failed to reset password. Please try again.");
        }
      } else {
        setIsSuccess(true);
        setMessage(
          t("password_reset_success_redirect") || "Password has been successfully reset! Redirecting to login..."
        );
        await auth.signOut();
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (err) {
      console.error("Password reset unexpected error:", err);
      setMessage(t("password_reset_unexpected_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const alertSx = message
    ? isSuccess
      ? authAlertSuccessSx(isDarkMode)
      : authAlertErrorSx(isDarkMode)
    : null;

  return (
    <>
      <CssBaseline />
      <Box sx={{ ...authPageOuterSx(isDarkMode), position: "relative", overflow: "hidden" }}>
        <AuthPageBottomAnimation isDarkMode={isDarkMode} />
        <AuthPageToggleBar />
        <Container maxWidth="md" sx={authPageContainerSx}>
          <Fade in timeout={800}>
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 440 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 4, mt: { xs: 1, sm: 0 }, width: "100%" }}>
                <Box
                  component="img"
                  src={isDarkMode ? "NewEraLogo.png" : "NewEraBlackLogo.png"}
                  alt="NewEra Logo"
                  sx={logoSx}
                />
              </Box>

              <Card sx={{ ...cardShellSx, width: "100%" }}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.875rem",
                        color: getMm(isDarkMode).onSurface,
                        mb: 1,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("password_reset_set_new_title") || "Set New Password"}
                    </Typography>
                    <Typography
                      sx={{
                        color: getMm(isDarkMode).onSurfaceVariant,
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("password_reset_set_new_subtitle") || "Enter your new password below"}
                    </Typography>
                  </Box>

                  {isValidatingLink && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={32} sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                    </Box>
                  )}

                  {message && !isValidatingLink && (
                    <Alert severity={isSuccess ? "success" : "error"} sx={{ mb: 3, ...alertSx }}>
                      {message}
                      {!isValidSession && (
                        <Box sx={{ mt: 1.5 }}>
                          <Button
                            component={Link}
                            to="/reset-password"
                            variant="outlined"
                            size="small"
                            startIcon={<LockResetIcon />}
                            sx={{ ...outlineButtonSx, textTransform: "none" }}
                          >
                            {t("password_reset_request_new_link") || "Request a new reset link"}
                          </Button>
                        </Box>
                      )}
                    </Alert>
                  )}

                  {isValidSession && !isValidatingLink && (
                    <>
                      <TextField
                        label={t("password_reset_new_password") || "New Password"}
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !isLoading && handleNewPassword()}
                        fullWidth
                        disabled={isLoading || isSuccess}
                        autoComplete="new-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword((v) => !v)}
                                edge="end"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                sx={{ color: getMm(isDarkMode).onSurfaceVariant }}
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ ...fieldSx, mb: 2 }}
                      />

                      <TextField
                        label={t("password_reset_confirm_password") || "Confirm New Password"}
                        type={showConfirmPassword ? "text" : "password"}
                        variant="outlined"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !isLoading && handleNewPassword()}
                        fullWidth
                        disabled={isLoading || isSuccess}
                        autoComplete="new-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword((v) => !v)}
                                edge="end"
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                sx={{ color: getMm(isDarkMode).onSurfaceVariant }}
                              >
                                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ ...fieldSx, mb: 2.5 }}
                      />

                      <Button
                        variant="contained"
                        onClick={handleNewPassword}
                        disabled={isLoading || isSuccess}
                        fullWidth
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ ...primaryButtonSx, py: 1.5, fontSize: "0.875rem", textTransform: "none" }}
                      >
                        {isLoading
                          ? t("password_reset_updating") || "Updating Password..."
                          : t("password_reset_update_button") || "Update Password"}
                      </Button>
                    </>
                  )}

                  {!isValidSession && !isValidatingLink && !message && (
                    <Alert severity="warning" sx={{ mb: 2, ...authAlertWarningSx(isDarkMode) }}>
                      {t("password_reset_link_invalid")}
                    </Alert>
                  )}

                  <Divider sx={{ my: 2.5, borderColor: getMm(isDarkMode).outlineVariant }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: getMm(isDarkMode).onSurfaceVariant,
                        px: 2,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("login_need_help")}
                    </Typography>
                  </Divider>

                  <Button
                    component={Link}
                    to="/login"
                    variant="outlined"
                    fullWidth
                    startIcon={<LoginIcon />}
                    sx={{ ...outlineButtonSx, textTransform: "none", justifyContent: "flex-start" }}
                  >
                    {t("password_reset_sign_in")}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
};

const PasswordReset = () => (
  <CustomThemeProvider>
    <PasswordResetContent />
  </CustomThemeProvider>
);

export default PasswordReset;
